/**
 * German Translations — Impact Core i18n
 *
 * Flat key-value structure. Keys use dot notation for namespacing.
 * Coverage: navigation, actions, statuses, dashboard labels, form labels.
 */

export const de: Record<string, string> = {
  // Navigation
  'nav.dashboard': 'Übersicht',
  'nav.funds': 'Fonds',
  'nav.investments': 'Investitionen',
  'nav.investees': 'Beteiligungen',
  'nav.esg': 'ESG Prüfung',
  'nav.compliance': 'Compliance',
  'nav.reports': 'Berichte',
  'nav.settings': 'Einstellungen',
  'nav.users': 'Benutzer',
  'nav.sfdr': 'SFDR',
  'nav.pai': 'PAI Indikatoren',
  'nav.taxonomy': 'EU-Taxonomie',
  'nav.filings': 'Meldungen',
  'nav.calendar': 'Kalender',
  'nav.submit': 'Daten einreichen',
  'nav.submissions': 'Meine Einreichungen',
  'nav.benchmarks': 'Benchmarks',
  'nav.messages': 'Nachrichten',
  'nav.portfolio': 'Portfolio',
  'nav.performance': 'Fondsperformance',
  'nav.esgSummary': 'ESG Zusammenfassung',
  'nav.pipeline': 'Pipeline',
  'nav.dueDiligence': 'Due Diligence',
  'nav.impact': 'Wirkungskennzahlen',
  'nav.sdg': 'SDG-Zuordnung',
  'nav.audit': 'Prüfprotokoll',
  'nav.snapshots': 'Daten-Snapshots',
  'nav.knowledgeGraph': 'Wissensgraph',
  'nav.geographic': 'Geografisch',
  'nav.bi': 'BI Studio',
  'nav.ai': 'KI-Assistent',
  'nav.tasks': 'Aufgaben',
  'nav.csrd': 'CSRD',
  'nav.regulatory': 'Regulatorischer Monitor',

  // Common Actions
  'action.create': 'Erstellen',
  'action.edit': 'Bearbeiten',
  'action.delete': 'Löschen',
  'action.save': 'Speichern',
  'action.cancel': 'Abbrechen',
  'action.submit': 'Einreichen',
  'action.approve': 'Genehmigen',
  'action.reject': 'Ablehnen',
  'action.download': 'Herunterladen',
  'action.export': 'Exportieren',
  'action.import': 'Importieren',
  'action.filter': 'Filtern',
  'action.search': 'Suchen',
  'action.refresh': 'Aktualisieren',
  'action.close': 'Schließen',
  'action.back': 'Zurück',
  'action.next': 'Weiter',
  'action.previous': 'Vorherige',
  'action.viewAll': 'Alle anzeigen',
  'action.viewDetails': 'Details anzeigen',
  'action.generate': 'Generieren',
  'action.signOut': 'Abmelden',
  'action.addNew': 'Neu hinzufügen',
  'action.invite': 'Einladen',

  // Statuses
  'status.draft': 'Entwurf',
  'status.submitted': 'Eingereicht',
  'status.validated': 'Validiert',
  'status.approved': 'Genehmigt',
  'status.rejected': 'Abgelehnt',
  'status.pending': 'Ausstehend',
  'status.inProgress': 'In Bearbeitung',
  'status.overdue': 'Überfällig',
  'status.compliant': 'Konform',
  'status.filed': 'Gemeldet',
  'status.active': 'Aktiv',
  'status.inactive': 'Inaktiv',
  'status.closed': 'Geschlossen',
  'status.fundraising': 'Kapitalbeschaffung',
  'status.investing': 'Investieren',
  'status.harvesting': 'Ernte',

  // Dashboard Labels
  'dashboard.portfolioOverview': 'Portfolioübersicht',
  'dashboard.complianceStatus': 'Compliance-Status',
  'dashboard.collectionProgress': 'Erfassungsfortschritt',
  'dashboard.activityFeed': 'Aktivität',
  'dashboard.quickActions': 'Schnellaktionen',
  'dashboard.investmentPipeline': 'Investitions-Pipeline',
  'dashboard.totalFunds': 'Fonds gesamt',
  'dashboard.aum': 'AUM',
  'dashboard.activeInvestments': 'Aktive Investitionen',
  'dashboard.totalInvestees': 'Beteiligungen',
  'dashboard.recentActivity': 'Letzte Aktivität',

  // LP Dashboard Labels
  'lp.totalCommitted': 'Gesamt zugesagt',
  'lp.capitalCalled': 'Abgerufenes Kapital',
  'lp.distributed': 'Ausgeschüttet',
  'lp.netPosition': 'Nettoposition',
  'lp.tvpi': 'TVPI',
  'lp.dpi': 'DPI',
  'lp.fundPerformance': 'Fondsperformance',
  'lp.esgSummary': 'ESG Zusammenfassung',
  'lp.reportLibrary': 'Berichtsbibliothek',
  'lp.taxonomyAligned': 'Taxonomie-konform',
  'lp.paiCoverage': 'PAI Abdeckung',
  'lp.portfolioPerformance': 'LP Portfolioperformance',

  // Fund Labels
  'fund.name': 'Fondsname',
  'fund.code': 'Fondscode',
  'fund.vintage': 'Gründungsjahr',
  'fund.size': 'Fondsgröße',
  'fund.targetSize': 'Zielgröße',
  'fund.currency': 'Währung',
  'fund.strategy': 'Strategie',
  'fund.status': 'Status',
  'fund.sfdrCategory': 'SFDR Kategorie',
  'fund.geographyFocus': 'Geografischer Fokus',
  'fund.sectorFocus': 'Sektorfokus',

  // Form Labels
  'form.name': 'Name',
  'form.email': 'E-Mail',
  'form.country': 'Land',
  'form.amount': 'Betrag',
  'form.currency': 'Währung',
  'form.date': 'Datum',
  'form.description': 'Beschreibung',
  'form.notes': 'Notizen',
  'form.type': 'Typ',
  'form.period': 'Zeitraum',
  'form.year': 'Jahr',
  'form.required': 'Erforderlich',
  'form.optional': 'Optional',

  // Table Headers
  'table.fund': 'Fonds',
  'table.commitment': 'Zusage',
  'table.called': 'Abgerufen',
  'table.distributed': 'Ausgeschüttet',
  'table.tvpi': 'TVPI',
  'table.status': 'Status',
  'table.regulation': 'Regulierung',
  'table.filingType': 'Meldetyp',
  'table.reportingPeriod': 'Zeitraum',
  'table.createdAt': 'Erstellt',
  'table.actions': 'Aktionen',

  // Compliance
  'compliance.sfdr': 'SFDR',
  'compliance.taxonomy': 'EU-Taxonomie',
  'compliance.pai': 'PAI',
  'compliance.dataCompleteness': 'Datenvollständigkeit',
  'compliance.nextDeadline': 'Nächste Frist',

  // Reports
  'report.quarterly': 'Quartalsbericht',
  'report.annual': 'Jahresbericht',
  'report.paiStatement': 'PAI Erklärung',
  'report.taxonomyReport': 'Taxonomiebericht',
  'report.generatedAt': 'Erstellt am',
  'report.version': 'Version',

  // General
  'general.loading': 'Wird geladen...',
  'general.noData': 'Keine Daten verfügbar',
  'general.error': 'Ein Fehler ist aufgetreten',
  'general.retry': 'Erneut versuchen',
  'general.total': 'Gesamt',
  'general.page': 'Seite',
  'general.of': 'von',
  'general.showing': 'Anzeige',
  'general.results': 'Ergebnisse',
  'general.language': 'Sprache',
  'general.english': 'Englisch',
  'general.french': 'Französisch',

  // Tour
  'tour.takeTour': 'Tour starten',
  'tour.skip': 'Überspringen',
  'tour.next': 'Weiter',
  'tour.prev': 'Zurück',
  'tour.finish': 'Fertig',
};
